import React, { useEffect, useState, useRef } from 'react';
import { X, Coins, Check, ShieldCheck, CloudArrowUp, FileText, Trash, Clock, WarningCircle, XCircle, Briefcase, Storefront, Buildings, Laptop, Stack, DotsThreeOutline } from '@phosphor-icons/react';
import { Spinner } from '../ui/Spinner';
import { cn, getSession, getVerificationStates, setVerificationState } from '../../lib/utils';
interface VerifyIncomeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}
type Phase = 'form' | 'uploading' | 'reviewing' | 'success' | 'pending' | 'rejected' | 'expired';
const INCOME_TYPES: {
  value: string;
  label: string;
  description: string;
  Icon: React.ElementType;
}[] = [{
  value: 'angajat',
  label: 'Angajat',
  description: 'Contract de muncă',
  Icon: Briefcase
}, {
  value: 'pfa',
  label: 'PFA',
  description: 'Persoană Fizică Autorizată',
  Icon: Storefront
}, {
  value: 'srl',
  label: 'SRL',
  description: 'Administrator / Asociat',
  Icon: Buildings
}, {
  value: 'freelancer',
  label: 'Freelancer',
  description: 'Drepturi de autor',
  Icon: Laptop
}, {
  value: 'mix',
  label: 'Mix venituri',
  description: 'Mai multe surse',
  Icon: Stack
}, {
  value: 'altul',
  label: 'Altul',
  description: 'Pensie, dividende, etc.',
  Icon: DotsThreeOutline
}];
const DOC_TYPES_BY_INCOME: Record<string, {
  value: string;
  label: string;
}[]> = {
  angajat: [{
    value: 'payslip',
    label: 'Fluturaș de salariu (ultimele 3 luni)'
  }, {
    value: 'bank_statement',
    label: 'Extras de cont (ultimele 3 luni)'
  }, {
    value: 'anaf',
    label: 'Adeverință ANAF'
  }],
  pfa: [{
    value: 'tax_declaration',
    label: 'Declarație unică (212)'
  }, {
    value: 'bank_statement',
    label: 'Extras de cont (ultimele 6 luni)'
  }],
  srl: [{
    value: 'dividends',
    label: 'Hotărâre AGA / Extras dividende'
  }, {
    value: 'bank_statement',
    label: 'Extras de cont (ultimele 6 luni)'
  }],
  freelancer: [{
    value: 'contracts',
    label: 'Contracte de colaborare'
  }, {
    value: 'bank_statement',
    label: 'Extras de cont (ultimele 6 luni)'
  }],
  mix: [{
    value: 'bank_statement',
    label: 'Extras de cont (ultimele 6 luni)'
  }, {
    value: 'anaf',
    label: 'Adeverință ANAF'
  }, {
    value: 'other',
    label: 'Alte documente justificative'
  }],
  altul: [{
    value: 'bank_statement',
    label: 'Extras de cont (ultimele 6 luni)'
  }, {
    value: 'other',
    label: 'Document justificativ'
  }]
};
const MAX_FILE_MB = 10;
const ACCEPTED_EXT = '.pdf,.jpg,.jpeg,.png';
export function VerifyIncomeModal({
  open,
  onClose,
  onSuccess
}: VerifyIncomeModalProps) {
  const [phase, setPhase] = useState<Phase>('form');
  const [incomeType, setIncomeType] = useState<string>('');
  const [incomeTypeOpen, setIncomeTypeOpen] = useState(false);
  const [docType, setDocType] = useState<string>('');
  const [docTypeOpen, setDocTypeOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      const session = getSession();
      if (session?.email) {
        const states = getVerificationStates(session.email);
        const incState = states.income;
        if (incState?.status === 'pending') setPhase('pending');else if (incState?.status === 'rejected') setPhase('rejected');else if (incState?.status === 'expired') setPhase('expired');else {
          setPhase('form');
          setIncomeType('');
          setDocType('');
          setFiles([]);
          setError(null);
        }
      } else {
        setPhase('form');
        setIncomeType('');
        setDocType('');
        setFiles([]);
        setError(null);
      }
    }
  }, [open]);
  useEffect(() => {
    if (phase === 'uploading') {
      const t = setTimeout(() => setPhase('reviewing'), 1200);
      return () => clearTimeout(t);
    }
    if (phase === 'reviewing') {
      const t = setTimeout(() => {
        const session = getSession();
        if (session?.email) {
          setVerificationState(session.email, 'income', {
            status: 'pending',
            submittedAt: new Date().toISOString()
          });
        }
        setPhase('success');
      }, 1600);
      return () => clearTimeout(t);
    }
  }, [phase]);
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'form') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, phase, onClose]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  if (!open) return null;
  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const validFiles: File[] = [];
    let hasError = false;
    Array.from(newFiles).forEach(f => {
      const sizeMb = f.size / (1024 * 1024);
      if (sizeMb > MAX_FILE_MB) {
        setError(`Fișier prea mare. Maxim ${MAX_FILE_MB} MB per fișier.`);
        hasError = true;
        return;
      }
      const ext = f.name.split('.').pop()?.toLowerCase() || '';
      if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
        setError('Format acceptat: PDF, JPG, PNG.');
        hasError = true;
        return;
      }
      validFiles.push(f);
    });
    if (!hasError) {
      setError(null);
      setFiles(prev => [...prev, ...validFiles]);
    }
  };
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  const handleSubmit = () => {
    if (!incomeType || !docType || files.length === 0) return;
    setPhase('uploading');
  };
  const selectedIncomeLabel = INCOME_TYPES.find(t => t.value === incomeType)?.label || 'Alege sursa de venit';
  const availableDocTypes = incomeType ? DOC_TYPES_BY_INCOME[incomeType] || [] : [];
  const selectedDocLabel = availableDocTypes.find(d => d.value === docType)?.label || 'Alege tipul de document';
  const canClose = phase !== 'uploading' && phase !== 'reviewing';
  return <div role="dialog" aria-modal="true" aria-label="Verifică venit" className="fixed inset-0 z-50 bg-dark-1/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 lg:pl-[16rem] animate-in fade-in duration-200" onClick={() => canClose && onClose()} data-id="element-2032">
      <div className="w-full sm:max-w-xl bg-dark-2 sm:rounded-2xl rounded-t-2xl border-t sm:border border-dark-3 animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2033">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-3 shrink-0" data-id="element-2034">
          <h2 className="text-base font-semibold text-light-1" data-id="element-2035">
            Verifică venit
          </h2>
          <button type="button" onClick={onClose} disabled={!canClose} aria-label="Închide" className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer disabled:opacity-50" data-id="element-2036">
            <X size={18} weight="bold" data-id="element-2037" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-6" data-id="element-2038">
          {phase === 'form' && <>
              {/* Compact header */}
              <div className="flex items-center gap-3 mb-6" data-id="element-2039">
                <div className="shrink-0 w-10 h-10 rounded-full bg-lime-5 border border-lime-4 flex items-center justify-center" data-id="element-2040">
                  <Coins size={20} weight="regular" className="text-lime-1" data-id="element-2041" />
                </div>
                <div className="min-w-0" data-id="element-2042">
                  <h3 className="text-base font-semibold text-light-1" data-id="element-2043">
                    Dovedește-ți capacitatea de plată
                  </h3>
                  <p className="text-xs text-light-3 mt-0.5" data-id="element-2044">
                    Verificăm manual documentele în maxim 24h.
                  </p>
                </div>
              </div>

              {/* Income type cards */}
              <div className="space-y-2.5 mb-5" data-id="element-2045">
                <label className="text-xs font-medium text-light-2" data-id="element-2046">
                  Sursă de venit
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-2047">
                  {INCOME_TYPES.map(opt => {
                const isSelected = incomeType === opt.value;
                const Icon = opt.Icon;
                return <button key={opt.value} type="button" onClick={() => {
                  setIncomeType(opt.value);
                  setDocType(''); // reset doc type when income type changes
                }} aria-pressed={isSelected} className={cn('relative flex flex-col items-start gap-1.5 p-3 rounded-lg border text-left transition-colors cursor-pointer', isSelected ? 'bg-lime-5 border-lime-1' : 'bg-dark-1 border-dark-4 hover:border-light-4')} data-id="element-2048">
                        <div className={cn('w-8 h-8 rounded-md flex items-center justify-center transition-colors', isSelected ? 'bg-lime-1/15 text-lime-1' : 'bg-dark-3 text-light-2')} data-id="element-2049">
                          <Icon size={16} weight="regular" data-id="element-2050" />
                        </div>
                        <div className="min-w-0" data-id="element-2051">
                          <p className={cn('text-sm font-medium leading-tight', isSelected ? 'text-light-1' : 'text-light-1')} data-id="element-2052">
                            {opt.label}
                          </p>
                          <p className="text-[11px] text-light-3 mt-0.5 leading-tight" data-id="element-2053">
                            {opt.description}
                          </p>
                        </div>
                        {isSelected && <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-lime-1 flex items-center justify-center" data-id="element-2054">
                            <Check size={10} weight="bold" className="text-dark-1" data-id="element-2055" />
                          </div>}
                      </button>;
              })}
                </div>
              </div>

              {/* Doc type radio list */}
              {incomeType && <div className="space-y-2.5 mb-5 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-2056">
                  <label className="text-xs font-medium text-light-2" data-id="element-2057">
                    Tip document
                  </label>
                  <div className="space-y-1.5" data-id="element-2058">
                    {availableDocTypes.map(opt => {
                const isSelected = docType === opt.value;
                return <button key={opt.value} type="button" onClick={() => setDocType(opt.value)} aria-pressed={isSelected} className={cn('w-full flex items-center gap-3 px-3.5 py-3 rounded-lg border text-left transition-colors cursor-pointer', isSelected ? 'bg-lime-5 border-lime-1' : 'bg-dark-1 border-dark-4 hover:border-light-4')} data-id="element-2059">
                          <div className={cn('shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors', isSelected ? 'border-lime-1 bg-lime-1' : 'border-dark-4')} data-id="element-2060">
                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-dark-1" data-id="element-2061" />}
                          </div>
                          <span className={cn('text-sm font-medium flex-1', isSelected ? 'text-light-1' : 'text-light-2')} data-id="element-2062">
                            {opt.label}
                          </span>
                        </button>;
              })}
                  </div>
                </div>}

              {/* Dropzone / file preview */}
              {incomeType && docType && <div className="space-y-1.5 mb-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-2063">
                  <label className="text-xs font-medium text-light-2 flex items-center justify-between" data-id="element-2064">
                    <span data-id="element-2065">Documente ({files.length})</span>
                    {files.length > 0 && <button type="button" onClick={() => fileInputRef.current?.click()} className="text-lime-1 hover:text-lime-2 transition-colors cursor-pointer" data-id="element-2066">
                        + Adaugă fișier
                      </button>}
                  </label>

                  {files.length > 0 ? <div className="space-y-2" data-id="element-2067">
                      {files.map((file, idx) => <div key={idx} className="flex items-center gap-3 px-3.5 py-3 rounded-md bg-dark-1 border border-dark-4" data-id="element-2068">
                          <div className="shrink-0 w-10 h-10 rounded-md bg-dark-3 flex items-center justify-center" data-id="element-2069">
                            <FileText size={20} weight="regular" className="text-light-2" data-id="element-2070" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2071">
                            <p className="text-sm font-medium text-light-1 truncate" data-id="element-2072">
                              {file.name}
                            </p>
                            <p className="text-xs text-light-3" data-id="element-2073">
                              {(file.size / 1024).toFixed(0)} KB
                            </p>
                          </div>
                          <button type="button" onClick={() => removeFile(idx)} aria-label="Elimină fișier" className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-red-1 hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-2074">
                            <Trash size={16} weight="regular" data-id="element-2075" />
                          </button>
                        </div>)}
                      <input ref={fileInputRef} type="file" multiple accept={ACCEPTED_EXT} onChange={e => handleFiles(e.target.files)} className="hidden" data-id="element-2076" />
                    </div> : <label onDragOver={e => {
              e.preventDefault();
              setDragOver(true);
            }} onDragLeave={() => setDragOver(false)} onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }} className={cn('flex flex-col items-center justify-center text-center px-4 py-8 rounded-md border-2 border-dashed cursor-pointer transition-colors', dragOver ? 'border-lime-1 bg-lime-5' : 'border-dark-4 bg-dark-1 hover:border-light-4 hover:bg-dark-3')} data-id="element-2077">
                      <input ref={fileInputRef} type="file" multiple accept={ACCEPTED_EXT} onChange={e => handleFiles(e.target.files)} className="hidden" data-id="element-2078" />
                      <CloudArrowUp size={32} weight="regular" className="text-light-3 mb-2" data-id="element-2079" />
                      <p className="text-sm font-medium text-light-1 mb-0.5" data-id="element-2080">
                        Apasă pentru a încărca
                      </p>
                      <p className="text-xs text-light-3" data-id="element-2081">
                        sau trage fișierele aici
                      </p>
                      <p className="text-xs text-light-4 mt-2" data-id="element-2082">
                        PDF, JPG, PNG · max {MAX_FILE_MB} MB
                      </p>
                    </label>}
                  {error && <p className="text-xs text-red-1" data-id="element-2083">{error}</p>}
                </div>}

              {/* Privacy note */}
              <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-dark-1 border border-dark-3 mt-6" data-id="element-2084">
                <ShieldCheck size={16} weight="fill" className="text-lime-1 shrink-0 mt-0.5" data-id="element-2085" />
                <p className="text-xs text-light-3 leading-relaxed" data-id="element-2086">
                  Documentele sunt criptate end-to-end. Doar echipa de
                  verificare le poate vedea — nu apar pe profilul tău public.
                </p>
              </div>
            </>}

          {phase === 'uploading' && <div className="flex flex-col items-center text-center py-10" data-id="element-2087">
              <div className="w-14 h-14 rounded-full bg-dark-1 border border-dark-3 flex items-center justify-center mb-4" data-id="element-2088">
                <Spinner size="md" data-id="element-2089" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2090">
                Se încarcă documentele
              </h3>
              <p className="text-sm text-light-3" data-id="element-2091">Nu închide fereastra.</p>
            </div>}

          {phase === 'reviewing' && <div className="flex flex-col items-center text-center py-10" data-id="element-2092">
              <div className="w-14 h-14 rounded-full bg-dark-1 border border-dark-3 flex items-center justify-center mb-4" data-id="element-2093">
                <Spinner size="md" data-id="element-2094" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2095">
                Se procesează
              </h3>
              <p className="text-sm text-light-3 max-w-xs" data-id="element-2096">
                Echipa noastră primește documentele tale.
              </p>
            </div>}

          {phase === 'success' && <div className="flex flex-col items-center text-center py-4" data-id="element-2097">
              <div className="w-16 h-16 rounded-full bg-lime-5 border border-lime-4 flex items-center justify-center mb-4" data-id="element-2098">
                <Check size={32} weight="bold" className="text-lime-1" data-id="element-2099" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2100">
                Documente primite
              </h3>
              <p className="text-sm text-light-3 max-w-xs mb-4" data-id="element-2101">
                Verificăm documentele în maxim 24h. Vei primi o notificare când
                badge-ul apare pe profil.
              </p>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-dark-3 border border-dark-4 text-xs font-medium text-light-2" data-id="element-2102">
                <Spinner size="sm" data-id="element-2103" />
                În verificare
              </div>
            </div>}

          {phase === 'pending' && <div className="flex flex-col items-center text-center py-4" data-id="element-2104">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4" data-id="element-2105">
                <Clock size={32} weight="bold" className="text-amber-400" data-id="element-2106" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2107">
                Venit în verificare
              </h3>
              <p className="text-sm text-light-3 max-w-xs" data-id="element-2108">
                Documentele tale au fost trimise și sunt în curs de verificare.
                Acest proces durează de obicei sub 24h.
              </p>
            </div>}

          {phase === 'rejected' && <div className="flex flex-col items-center text-center py-4" data-id="element-2109">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4" data-id="element-2110">
                <WarningCircle size={32} weight="bold" className="text-red-400" data-id="element-2111" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2112">
                Verificare respinsă
              </h3>
              <p className="text-sm text-light-3 max-w-xs mb-4" data-id="element-2113">
                Din păcate, nu am putut valida documentele tale. Motivul:
                documentul nu este complet sau nu acoperă perioada necesară.
              </p>
            </div>}

          {phase === 'expired' && <div className="flex flex-col items-center text-center py-4" data-id="element-2114">
              <div className="w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-4" data-id="element-2115">
                <XCircle size={32} weight="bold" className="text-orange-400" data-id="element-2116" />
              </div>
              <h3 className="text-lg font-semibold text-light-1 mb-1" data-id="element-2117">
                Verificare expirată
              </h3>
              <p className="text-sm text-light-3 max-w-xs mb-4" data-id="element-2118">
                Verificarea veniturilor tale a expirat. Te rugăm să încarci
                documente recente pentru a-ți reînnoi badge-ul.
              </p>
            </div>}
        </div>

        {/* Footer */}
        {phase === 'form' && <div className="px-5 py-4 border-t border-dark-3 shrink-0" data-id="element-2119">
            <button type="button" onClick={handleSubmit} disabled={!incomeType || !docType || files.length === 0} className={cn('w-full inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors', incomeType && docType && files.length > 0 ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-2120">
              Trimite spre verificare
            </button>
          </div>}
        {phase === 'success' && <div className="px-5 py-4 border-t border-dark-3 shrink-0" data-id="element-2121">
            <button type="button" onClick={onSuccess} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2122">
              Gata
            </button>
          </div>}
        {phase === 'pending' && <div className="px-5 py-4 border-t border-dark-3 shrink-0" data-id="element-2123">
            <button type="button" onClick={onClose} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2124">
              Închide
            </button>
          </div>}
        {(phase === 'rejected' || phase === 'expired') && <div className="px-5 py-4 border-t border-dark-3 shrink-0" data-id="element-2125">
            <button type="button" onClick={() => {
          setPhase('form');
          setIncomeType('');
          setDocType('');
          setFiles([]);
        }} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm transition-colors cursor-pointer" data-id="element-2126">
              Încearcă din nou
            </button>
          </div>}
      </div>
    </div>;
}